import {supportsCpsCodeVault} from "@/core/services/shellCapabilities";

/**
 * File the code porteur of a CPS card in the operating system's credential
 * store, through the shell.
 *
 * The code goes one way: this renderer, deployed from the web, files it, asks
 * whether one is kept, or drops it — it never reads it back. The shell reads
 * it from the store itself, to present it to the card.
 */
const useCpsCode = () => {
    /**
     * Whether this shell has a credential store to file a code in.
     */
    const isAvailable = (): boolean => supportsCpsCodeVault();

    /**
     * File the code porteur of a card holder.
     *
     * @param code The code porteur to keep.
     * @returns Whether the code is now in the store.
     */
    const keep = async (code: string): Promise<boolean> => {
        if (!supportsCpsCodeVault()) {
            return false;
        }

        return await window.ipcRenderer.storeCpsCode(code) as boolean;
    };

    /**
     * Whether this workstation already keeps a code porteur.
     */
    const isKept = async (): Promise<boolean> => {
        if (!supportsCpsCodeVault()) {
            return false;
        }

        return await window.ipcRenderer.hasCpsCode() as boolean;
    };

    /**
     * Drop the code porteur this workstation keeps.
     */
    const forget = async (): Promise<boolean> => {
        if (!supportsCpsCodeVault()) {
            return false;
        }

        return await window.ipcRenderer.forgetCpsCode() as boolean;
    };

    return {
        isAvailable,
        keep,
        isKept,
        forget
    };
};

export default useCpsCode;
